// src/utils/validationRules.ts
// Reusable validation patterns and regex rules

/**
 * Common regex patterns for validation
 */
export const VALIDATION_PATTERNS = {
  // Basic email validation - matches pattern used in ClientForm
  email: /\S+@\S+\.\S+/,

  // Phone number patterns (can be extended based on requirements)
  phone: {
    // Basic phone number (10+ digits with optional formatting)
    basic: /^[\d\s\-+()]{10,}$/,
    // South African mobile (starts with 0, 10 digits)
    southAfrican: /^0\d{9}$/,
    // International format
    international: /^\+\d{10,15}$/,
  },

  // ID Number patterns (can be extended for specific countries)
  idNumber: {
    // South African ID (13 digits)
    southAfrican: /^\d{13}$/,
    // Generic (alphanumeric, 5-20 characters)
    generic: /^[A-Z0-9]{5,20}$/i,
  },

  // Numeric patterns
  positiveNumber: /^\d*\.?\d+$/,
  integer: /^\d+$/,
  currency: /^\d+(\.\d{1,2})?$/,

  // Name patterns (letters, spaces, hyphens, apostrophes)
  name: /^[a-zA-Z\s\-']+$/,

  // Alphanumeric with spaces
  alphanumericWithSpaces: /^[a-zA-Z0-9\s]+$/,
};

/**
 * Validation rule functions that return boolean
 */
export const VALIDATION_RULES = {
  /**
   * Check if a string is not empty (after trimming)
   */
  isRequired: (value: string | undefined | null): boolean => {
    return !!value && value.trim().length > 0;
  },

  /**
   * Check if a value is a valid email
   */
  isValidEmail: (email: string): boolean => {
    return VALIDATION_PATTERNS.email.test(email);
  },

  /**
   * Check if a value is a valid phone number (basic validation)
   */
  isValidPhone: (phone: string): boolean => {
    return VALIDATION_PATTERNS.phone.basic.test(phone);
  },

  /**
   * Check if a value is a positive number
   */
  isPositiveNumber: (value: string | number): boolean => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return !isNaN(num) && num > 0;
  },

  /**
   * Check if a value is a valid integer
   */
  isValidInteger: (value: string | number): boolean => {
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    return !isNaN(num) && Number.isInteger(num);
  },

  /**
   * Check if a string meets minimum length requirement
   */
  hasMinLength: (value: string, minLength: number): boolean => {
    return value.trim().length >= minLength;
  },

  /**
   * Check if a string doesn't exceed maximum length
   */
  hasMaxLength: (value: string, maxLength: number): boolean => {
    return value.trim().length <= maxLength;
  },

  /**
   * Check if a date string is valid
   */
  isValidDate: (dateString: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  },

  /**
   * Check if endDate is after startDate
   */
  isDateAfter: (startDate: string, endDate: string): boolean => {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return end > start;
  },

  /**
   * Check if a date is not in the future
   */
  isNotFutureDate: (dateString: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return date <= today;
  },

  /**
   * Check if a date is not in the past
   */
  isNotPastDate: (dateString: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day
    return date >= today;
  },

  /**
   * Check if a value is a valid name (letters, spaces, hyphens, apostrophes)
   */
  isValidName: (name: string): boolean => {
    return VALIDATION_PATTERNS.name.test(name);
  },

  /**
   * Check if a value contains only alphanumeric characters and spaces
   */
  isAlphanumeric: (value: string): boolean => {
    return VALIDATION_PATTERNS.alphanumericWithSpaces.test(value);
  },

  /**
   * Check if a select/dropdown value is selected (not empty or default)
   */
  isSelected: (value: string | undefined | null): boolean => {
    return !!value && value.trim().length > 0 && value !== '';
  },
};

/**
 * Conditional validation helpers
 */
export const CONDITIONAL_RULES = {
  /**
   * Validate a field only if a condition is true
   */
  validateIf: (condition: boolean, validationFn: () => boolean): boolean => {
    return !condition || validationFn();
  },

  /**
   * Require a field only if another field has a specific value
   */
  requiredIf: (
    value: string | undefined | null,
    condition: boolean
  ): boolean => {
    return !condition || VALIDATION_RULES.isRequired(value);
  },

  /**
   * Validate field A if field B is filled
   */
  requiredIfOtherHasValue: (
    valueToValidate: string | undefined | null,
    otherValue: string | undefined | null
  ): boolean => {
    const otherHasValue = VALIDATION_RULES.isRequired(otherValue);
    return !otherHasValue || VALIDATION_RULES.isRequired(valueToValidate);
  },
};

/**
 * Composite validation functions for common scenarios
 */
export const COMPOSITE_VALIDATIONS = {
  /**
   * Validate email field (required + format)
   */
  validateEmail: (email: string | undefined): boolean => {
    if (!VALIDATION_RULES.isRequired(email)) return false;
    return VALIDATION_RULES.isValidEmail(email!);
  },

  /**
   * Validate phone field (required + format)
   */
  validatePhone: (phone: string | undefined): boolean => {
    if (!VALIDATION_RULES.isRequired(phone)) return false;
    return VALIDATION_RULES.isValidPhone(phone!);
  },

  /**
   * Validate required select field
   */
  validateRequiredSelect: (value: string | undefined): boolean => {
    return VALIDATION_RULES.isSelected(value);
  },

  /**
   * Validate date range (both required + end after start)
   */
  validateDateRange: (startDate: string, endDate: string): boolean => {
    if (!VALIDATION_RULES.isRequired(startDate)) return false;
    if (!VALIDATION_RULES.isRequired(endDate)) return false;
    return VALIDATION_RULES.isDateAfter(startDate, endDate);
  },

  /**
   * Validate currency amount (required + positive + max 2 decimals)
   */
  validateCurrency: (amount: string | number): boolean => {
    if (!VALIDATION_RULES.isRequired(String(amount))) return false;
    return VALIDATION_RULES.isPositiveNumber(amount);
  },
};

/**
 * Error message generators that combine rules with messages
 */
export const generateValidationError = {
  /**
   * Generate error for required field
   */
  forRequired: (
    value: string | undefined,
    fieldName: string
  ): string | undefined => {
    return VALIDATION_RULES.isRequired(value)
      ? undefined
      : `${fieldName} is required`;
  },

  /**
   * Generate error for email field
   */
  forEmail: (email: string | undefined): string | undefined => {
    if (!VALIDATION_RULES.isRequired(email)) return 'Email is required';
    if (!VALIDATION_RULES.isValidEmail(email!)) return 'Email is invalid';
    return undefined;
  },

  /**
   * Generate error for phone field
   */
  forPhone: (phone: string | undefined): string | undefined => {
    if (!VALIDATION_RULES.isRequired(phone)) return 'Phone number is required';
    if (!VALIDATION_RULES.isValidPhone(phone!))
      return 'Phone number is invalid';
    return undefined;
  },

  /**
   * Generate error for date range
   */
  forDateRange: (
    startDate: string,
    endDate: string,
    startLabel: string,
    endLabel: string
  ): string | undefined => {
    if (!VALIDATION_RULES.isRequired(startDate))
      return `${startLabel} is required`;
    if (!VALIDATION_RULES.isRequired(endDate)) return `${endLabel} is required`;
    if (!VALIDATION_RULES.isDateAfter(startDate, endDate)) {
      return `${endLabel} must be after ${startLabel}`;
    }
    return undefined;
  },
};
